/**
 * Attribute insights URL state. One screen, two views:
 *
 *   /attribute-insights           Top insights   ?metric&tab&comparison
 *   /attribute-insights/explore   By attribute   ?metric&tab&attribute&value&page
 *
 * Both start from the shared Focus scope (apps, dates, filters; no status,
 * thresholds or 24-hour activity — attribute screens never wrote them, parity
 * §0). Own keys:
 *  - `metric`     the ranking metric, apps/web's spelling (`cpi`, `roas:d7`);
 *  - `tab`        the app tab when several apps are ticked (one feed per app);
 *  - `comparison` the open Comparison drawer: the card's selection JSON, as
 *                 `insight.action.query.comparison` carries it. The page's own
 *                 scope IS the comparison's scope (an old link's `scope` is
 *                 applied to the page by the redirect);
 *  - `attribute`, `value`, `page` — By attribute's attribute (by name), value
 *                 (its filterable spelling) and 1-based page of 8 values.
 */
import { defineFocusConfig, parseFocusSearch, type FocusControls, type FocusSearch } from '@/features/focus';
import { defineSearch, param, type RawSearch } from '@/router/search';

export const INSIGHTS_CONTROLS = {
  allApps: false,
  dates: true,
  filters: { status: false, thresholds: false, activity: false, assetTypes: false },
} as const satisfies FocusControls;

export interface InsightsSearch extends FocusSearch {
  metric?: string | undefined;
  tab?: string | undefined;
  comparison?: string | undefined;
  /** One-shot deep link from another screen: `open=country_filter` opens Filters on
   * Country (e.g. Creative lab's "Filter by country"). Removed from the URL once used. */
  open?: 'country_filter' | undefined;
}

export interface ExploreSearch extends InsightsSearch {
  attribute?: string | undefined;
  value?: string | undefined;
  page?: number | undefined;
}

const own = (raw: RawSearch) => ({
  metric: param.string(raw, 'metric'),
  tab: param.string(raw, 'tab'),
  open: param.string(raw, 'open') === 'country_filter' ? ('country_filter' as const) : undefined,
});

/** Insights always compare one format. Old mixed links resolve to their first valid type. */
const insightScope = (raw: RawSearch): FocusSearch => {
  const scope = parseFocusSearch(raw, INSIGHTS_CONTROLS);
  return { ...scope, asset_type: [scope.asset_type[0] ?? 'video'] };
};

export const parseInsightsSearch = (raw: RawSearch): InsightsSearch => ({
  ...insightScope(raw),
  ...own(raw),
  comparison: param.string(raw, 'comparison'),
});

export const parseExploreSearch = (raw: RawSearch): ExploreSearch => ({
  ...insightScope(raw),
  ...own(raw),
  comparison: param.string(raw, 'comparison'),
  // Legacy apps/web keys are accepted and dropped (the router redirect rewrites them).
  attribute: param.string(raw, 'attribute') ?? param.string(raw, 'attribute_filter_key'),
  value: param.string(raw, 'value') ?? param.string(raw, 'attribute_filter_value'),
  page: param.number(raw, 'page', { min: 1, integer: true }),
});

export const insightsSearch = defineSearch(parseInsightsSearch);
export const exploreSearch = defineSearch(parseExploreSearch);

const APP_SWITCHER = {
  evidenceFor: 'statuses and insights',
  hint: 'Click an app to look at it alone, or tick several to see their insights side by side.',
  portfolioOnPerformance: true,
} as const;

export const INSIGHTS_FOCUS = defineFocusConfig({
  ...INSIGHTS_CONTROLS,
  parse: parseInsightsSearch,
  appSwitcher: APP_SWITCHER,
});

export const EXPLORE_FOCUS = defineFocusConfig({
  ...INSIGHTS_CONTROLS,
  parse: parseExploreSearch,
  // A new scope ranks new numbers: keep the value (when it still exists) but start on its page.
  onScopeChange: (next: ExploreSearch) => ({ ...next, page: undefined }),
  appSwitcher: APP_SWITCHER,
});

/** The scope keys only (apps, dates, filters): what carries between the two views. */
export const scopeKeysOf = (search: FocusSearch): FocusSearch => parseFocusSearch(search as unknown as RawSearch, INSIGHTS_CONTROLS);

/** The app tab in view: the URL's when it is one of the selected apps, else the first app. */
export const activeTab = (appIds: readonly string[], tab: string | undefined): string | undefined =>
  tab && appIds.includes(tab) ? tab : appIds[0];
